import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { LtiSetupWizardComponent } from '../lti-setup-wizard/lti-setup-wizard.component';
import {
  LmsRegistration,
  LmsRegistryService,
  LtiRegistrationTokenCreated,
} from '../../../services/lms-registry.service';

/** Die drei Einstiegskacheln. `dynamic: false` führt in den Wizard, weil das
 *  LMS Dynamic Registration nicht beherrscht. */
interface LmsChoice {
  key: 'moodle' | 'ilias' | 'other';
  dynamic: boolean;
  /** Echtes Produktlogo aus `assets/images/integrations`, dieselben Dateien
   *  wie auf der Integrationsseite. Nebular-Iconnamen wären hier falsch —
   *  ein Preis-Abzeichen für ILIAS erkennt niemand wieder. */
  image: string;
  beta?: boolean;
}

/**
 * LTI-1.3-Verbindungen eines Mandanten: anlegen, Status sehen, pausieren,
 * trennen, löschen.
 *
 * Zwei Wege zum Anlegen:
 *   • **Dynamic Registration** (Moodle 4+, ILIAS 11) — wir erzeugen eine
 *     Registrierungs-URL, der Admin fügt sie im LMS ein, beide Seiten tauschen
 *     ihre Konfiguration selbst aus.
 *   • **Manuell** (ältere Versionen, Open edX) — der bestehende vierstufige
 *     {@link LtiSetupWizardComponent}.
 *
 * Visuelle Sprache bewusst die der Moodle-Kachel aus
 * `moodle-connect-dialog.component`: Kopfzeile mit Statuspunkt, Chip-Reihe,
 * Hinweisbanner, Einstellungsgruppe. Wer beide Dialoge kennt, findet sich hier
 * ohne Nachdenken zurecht.
 */
@Component({
  selector: 'app-lti-connect-dialog',
  templateUrl: './lti-connect-dialog.component.html',
  styleUrl: './lti-connect-dialog.component.scss',
})
export class LtiConnectDialogComponent implements OnInit, OnDestroy {
  registrations: LmsRegistration[] = [];
  loading = true;

  /** Welche Kachel gerade aufgeklappt ist; null = Übersicht. */
  activeChoice: LmsChoice | null = null;
  /** Frisch erzeugte Registrierungs-URL. Nur einmal abrufbar, deshalb bleibt
   *  sie im Speicher der Komponente, bis der Dialog schließt. */
  issuedUrl: string | null = null;
  issuing = false;
  environment: 'production' | 'staging' = 'production';
  label = '';

  /** Registrierung, deren Tool-URLs gerade aufgeklappt sind. */
  urlsOpenFor: string | null = null;
  busyId: string | null = null;
  errorMessage: string | null = null;

  /** Wie viele Anleitungsschritte je Plattform existieren.
   *
   *  ILIAS braucht doppelt so viele wie Moodle, und das liegt nicht an uns:
   *  ILIAS' Registrierungsroutine speichert zwar unsere Deep-Linking-URL,
   *  setzt den zugehörigen Schalter aber nie, und der entstehende Anbieter ist
   *  zunächst nur für den anlegenden Benutzer sichtbar. Beides muss von Hand
   *  nachgezogen werden, sonst erscheint keine Agent-Auswahl beziehungsweise
   *  bräuchte jede Lehrkraft eine eigene Registrierungs-URL. */
  private readonly guideSteps: Record<string, number> = {
    moodle: 4,
    ilias: 8,
    other: 0,
  };

  /** Die Anleitung ist eingeklappt, bis jemand sie aufklappt. Sie steht über
   *  dem Formular und würde sonst bei jedem Besuch den Blick auf die
   *  Registrierungs-URL verstellen. */
  guideOpen = false;

  toggleGuide(): void {
    this.guideOpen = !this.guideOpen;
  }

  /** Schrittnummern für die aktive Kachel. */
  get guideStepNumbers(): number[] {
    const count = this.guideSteps[this.activeChoice?.key ?? ''] ?? 0;
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  readonly choices: LmsChoice[] = [
    { key: 'moodle', dynamic: true, image: '/assets/images/integrations/moodle.png' },
    // Kein Beta-Abzeichen mehr: ILIAS 11 ist seit 04.05.2026 regulär
    // verfügbar. Die Einschränkung liegt nicht am Reifegrad, sondern an der
    // Version — 9 und 10 können keine spec-konforme dynamische Registrierung.
    { key: 'ilias', dynamic: true, image: '/assets/images/integrations/ilias.png' },
    { key: 'other', dynamic: false, image: '/assets/images/integrations/lti.svg' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private lms: LmsRegistryService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private t: TranslateService,
    protected dialogRef: NbDialogRef<LtiConnectDialogComponent>,
    private router: Router,
  ) {}

  /** Von der LTI-Verbindung zum Datenabgleich.
   *
   *  Beides sind getrennte Schritte: LTI bringt den Agent in den Kurs,
   *  der Abgleich bringt Kurse, Personen und Lernstände zu uns. Wer nur
   *  ersteres will, ist hier fertig — wer beides braucht, soll den zweiten
   *  Schritt nicht suchen müssen.
   */
  goToIliasSync(): void {
    this.dialogRef.close(true);
    this.router.navigate(['/ilias']);
  }

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.loading = true;
    this.lms
      .listRegistrations('lti13')
      .pipe(takeUntil(this.destroy$), catchError(() => of([] as LmsRegistration[])))
      .subscribe((rows) => {
        this.registrations = rows;
        this.loading = false;
      });
  }

  // ── Anzeige ───────────────────────────────────────────────────────
  issuerHost(reg: LmsRegistration): string {
    const raw = reg.lti_issuer || '';
    try {
      return new URL(raw).hostname || raw;
    } catch {
      return raw.replace(/^https?:\/\//, '').replace(/\/+$/, '') || reg.display_name;
    }
  }

  /** Nebular-Status für Punkt und Chip. `incomplete` ist bewusst eine
   *  Warnung und kein Fehler — die Verbindung ist nicht kaputt, sie wurde
   *  nur nie fertig eingerichtet. */
  statusOf(reg: LmsRegistration): 'success' | 'warning' | 'danger' {
    switch (reg.connection_status) {
      case 'connected':
      case 'ready':
        return 'success';
      case 'paused':
      case 'incomplete':
        return 'warning';
      default:
        return 'danger';
    }
  }

  statusKey(reg: LmsRegistration): string {
    return `dialogs.ltiConnect.status.${reg.connection_status || 'unknown'}`;
  }

  platformLabel(reg: LmsRegistration): string | null {
    const product = (reg.lti_platform_product || '').trim();
    if (!product) return null;
    const pretty = product.charAt(0).toUpperCase() + product.slice(1);
    return reg.lti_platform_version ? `${pretty} ${reg.lti_platform_version}` : pretty;
  }

  isPaused(reg: LmsRegistration): boolean {
    return reg.connection_status === 'paused';
  }

  isDisconnected(reg: LmsRegistration): boolean {
    return reg.connection_status === 'disconnected';
  }

  canRefresh(reg: LmsRegistration): boolean {
    return reg.lti_registration_mode === 'dynamic';
  }

  toolUrls(reg: LmsRegistration): { labelKey: string; value: string }[] {
    return [
      { labelKey: 'tutor.ltiWizard.step2.urls.toolUrl', value: reg.lti_target_link_uri || '' },
      { labelKey: 'tutor.ltiWizard.step2.urls.loginUrl', value: reg.lti_login_url || '' },
      { labelKey: 'tutor.ltiWizard.step2.urls.jwksUrl', value: reg.lti_jwks_url || '' },
    ].filter((row) => !!row.value);
  }

  toggleUrls(reg: LmsRegistration): void {
    this.urlsOpenFor = this.urlsOpenFor === reg.id ? null : reg.id;
  }

  copy(value: string, ev?: MouseEvent): void {
    navigator.clipboard?.writeText(value);
    const el = ev?.target;
    if (el instanceof HTMLElement) {
      const original = el.textContent;
      el.textContent = '✓';
      setTimeout(() => { el.textContent = original; }, 800);
    }
  }

  // ── Anlegen ───────────────────────────────────────────────────────
  chooseLms(choice: LmsChoice): void {
    this.errorMessage = null;
    this.issuedUrl = null;
    if (!choice.dynamic) {
      this.openWizard();
      return;
    }
    this.activeChoice = choice;
    this.guideOpen = false;
  }

  cancelChoice(): void {
    this.activeChoice = null;
    this.guideOpen = false;
    this.issuedUrl = null;
    this.label = '';
    this.environment = 'production';
  }

  issueUrl(): void {
    if (!this.activeChoice || this.issuing) return;
    this.issuing = true;
    this.errorMessage = null;
    this.lms
      .createLtiRegistrationToken({
        label: this.label.trim() || undefined,
        environment: this.environment,
        lms_hint: this.activeChoice.key,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (token: LtiRegistrationTokenCreated) => {
          this.issuing = false;
          this.issuedUrl = token.registration_url;
        },
        error: (err) => {
          this.issuing = false;
          this.errorMessage = this.resolveError(err);
        },
      });
  }

  openWizard(): void {
    this.dialogService
      .open(LtiSetupWizardComponent, { hasScroll: true, closeOnBackdropClick: false })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cancelChoice();
        this.reload();
      });
  }

  // ── Lebenszyklus ──────────────────────────────────────────────────
  togglePause(reg: LmsRegistration): void {
    if (this.busyId) return;
    this.busyId = reg.id;
    const call$ = this.isPaused(reg)
      ? this.lms.resumeRegistration(reg.id)
      : this.lms.pauseRegistration(reg.id);
    call$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.busyId = null;
        this.toastr.success(
          this.t.instant(
            this.isPaused(reg)
              ? 'dialogs.ltiConnect.resumedToast'
              : 'dialogs.ltiConnect.pausedToast',
          ),
          '',
        );
        this.reload();
      },
      error: (err) => {
        this.busyId = null;
        this.toastr.danger(this.resolveError(err), '');
      },
    });
  }

  refresh(reg: LmsRegistration): void {
    if (this.busyId) return;
    this.busyId = reg.id;
    this.lms
      .refreshLtiPlatformConfig(reg.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.busyId = null;
          this.toastr.success(
            this.t.instant(
              result.changed_fields.length
                ? 'dialogs.ltiConnect.refreshedToast'
                : 'dialogs.ltiConnect.refreshedUnchangedToast',
              { count: result.changed_fields.length },
            ),
            '',
          );
          this.reload();
        },
        error: (err) => {
          this.busyId = null;
          this.toastr.danger(this.resolveError(err), '');
        },
      });
  }

  disconnect(reg: LmsRegistration): void {
    this.confirmThen(
      'dialogs.ltiConnect.disconnectDialog.title',
      'dialogs.ltiConnect.disconnectDialog.message',
      () => {
        this.busyId = reg.id;
        this.lms.disconnectRegistration(reg.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.busyId = null;
            this.toastr.success(this.t.instant('dialogs.ltiConnect.disconnectedToast'), '');
            this.reload();
          },
          error: (err) => {
            this.busyId = null;
            this.toastr.danger(this.resolveError(err), '');
          },
        });
      },
    );
  }

  remove(reg: LmsRegistration): void {
    this.confirmThen(
      'dialogs.ltiConnect.deleteDialog.title',
      'dialogs.ltiConnect.deleteDialog.message',
      () => {
        this.busyId = reg.id;
        this.lms.deleteRegistration(reg.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.busyId = null;
            this.toastr.success(this.t.instant('dialogs.ltiConnect.deletedToast'), '');
            this.reload();
          },
          error: (err) => {
            this.busyId = null;
            this.toastr.danger(this.resolveError(err), '');
          },
        });
      },
    );
  }

  private confirmThen(titleKey: string, messageKey: string, run: () => void): void {
    this.dialogService
      .open(ConfirmDialogComponent, {
        hasScroll: true,
        closeOnBackdropClick: true,
        context: { title: titleKey, message: messageKey },
      })
      .onClose.pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (confirmed) run();
      });
  }

  private resolveError(err: any): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object') {
      return detail.message || detail.code || this.t.instant('toasts.error');
    }
    return err?.message || this.t.instant('toasts.error');
  }

  close(): void {
    this.dialogRef.close();
  }

  trackReg = (_: number, reg: LmsRegistration) => reg.id;
}
